import * as React from "react"
import { TableHead } from "@/components/ui/table"
import {
  clampColumnWidth,
  deserializeColumnWidths,
  serializeColumnWidths,
  type ColumnWidthDefinition,
  type ColumnWidths,
} from "@/lib/column-widths"
import { cn } from "@/lib/utils"

export function usePersistentColumnWidths(
  storageKey: string,
  definitions: readonly ColumnWidthDefinition[],
) {
  const [widths, setWidths] = React.useState<ColumnWidths>(() => {
    if (typeof window === "undefined") {
      return deserializeColumnWidths(null, definitions)
    }

    try {
      return deserializeColumnWidths(window.localStorage.getItem(storageKey), definitions)
    } catch {
      return deserializeColumnWidths(null, definitions)
    }
  })

  React.useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, serializeColumnWidths(widths))
    } catch {
      // Storage can be unavailable in privacy modes. Resizing still works for this session.
    }
  }, [storageKey, widths])

  const resizeColumn = React.useCallback(
    (columnId: string, width: number) => {
      const definition = definitions.find((item) => item.id === columnId)
      if (!definition) return

      setWidths((current) => ({
        ...current,
        [columnId]: clampColumnWidth(definition, width),
      }))
    },
    [definitions],
  )

  return { widths, resizeColumn }
}

export function ResizableTableHead({
  definition,
  width,
  onWidthChange,
  label,
  className,
  children,
}: {
  definition: ColumnWidthDefinition
  width: number
  onWidthChange: (width: number) => void
  label: string
  className?: string
  children: React.ReactNode
}) {
  const drag = React.useRef<{ startX: number; startWidth: number } | null>(null)

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    drag.current = { startX: event.clientX, startWidth: width }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current || !event.currentTarget.hasPointerCapture(event.pointerId)) return
    onWidthChange(drag.current.startWidth + event.clientX - drag.current.startX)
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    drag.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    event.preventDefault()
    const change = event.shiftKey ? 25 : 10
    onWidthChange(width + (event.key === "ArrowRight" ? change : -change))
  }

  return (
    <TableHead
      className={cn("relative select-none", className)}
      style={{ width, minWidth: width, maxWidth: width }}
    >
      <span className="block truncate">{children}</span>
      <div
        role="separator"
        aria-label={`Resize ${label} column`}
        aria-orientation="vertical"
        aria-keyshortcuts="ArrowLeft ArrowRight"
        aria-valuemin={definition.minWidth}
        aria-valuemax={definition.maxWidth}
        aria-valuenow={width}
        tabIndex={0}
        className="absolute inset-y-0 -right-1.5 z-10 w-3 cursor-col-resize touch-none outline-none after:absolute after:inset-y-2 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-border hover:after:bg-primary focus-visible:after:bg-ring"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={() => onWidthChange(definition.defaultWidth)}
        onKeyDown={handleKeyDown}
      />
    </TableHead>
  )
}

export function ResizableColGroup({
  definitions,
  widths,
}: {
  definitions: readonly ColumnWidthDefinition[]
  widths: ColumnWidths
}) {
  return (
    <colgroup>
      {definitions.map((definition) => (
        <col key={definition.id} style={{ width: widths[definition.id] }} />
      ))}
    </colgroup>
  )
}

export function getTableWidth(
  definitions: readonly ColumnWidthDefinition[],
  widths: ColumnWidths,
): number {
  return definitions.reduce(
    (total, definition) => total + (widths[definition.id] ?? definition.defaultWidth),
    0,
  )
}
